import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, RotateCcw, Sparkles, Save, Check, LogIn, Share2, Copy, Instagram, Mail } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURL_IMAGES = {
  wavy: "/quiz/wavy.jpg",
  curly: "/quiz/curly.jpg",
  coily: "/quiz/coily.jpg",
};

const QUESTIONS = [
  {
    id: "curl_type",
    question: "What's your curl type?",
    subtitle: "Not sure? Pick the one that looks closest to your natural texture.",
    hasImages: true,
    options: [
      { value: "wavy", label: "Wavy", desc: "Loose S-shaped waves (2A–2C)", image: CURL_IMAGES.wavy },
      { value: "curly", label: "Curly", desc: "Defined spirals and ringlets (3A–3C)", image: CURL_IMAGES.curly },
      { value: "coily", label: "Coily", desc: "Tight coils and z-patterns (4A–4C)", image: CURL_IMAGES.coily },
    ],
  },
  {
    id: "concern",
    question: "What's your main hair concern?",
    subtitle: "Pick the one that bothers you most day-to-day.",
    options: [
      { value: "frizz", label: "Frizz", desc: "My hair poofs up, especially in humidity" },
      { value: "dryness", label: "Dryness", desc: "My hair feels rough, straw-like or brittle" },
      { value: "definition", label: "Definition", desc: "I want more bounce and clump to my curls" },
      { value: "volume", label: "Volume", desc: "My curls fall flat or feel weighed down" },
    ],
  },
  {
    id: "styling",
    question: "How do you usually style your hair?",
    subtitle: "Think about your most common wash-day routine.",
    options: [
      { value: "wash_go", label: "Wash & Go", desc: "Apply products and let curls do their thing" },
      { value: "diffuse", label: "Diffuse", desc: "I use a diffuser attachment on my dryer" },
      { value: "air_dry", label: "Air Dry", desc: "I let my hair dry naturally, no heat" },
      { value: "protective", label: "Protective Styles", desc: "Braids, twists, buns — low manipulation" },
    ],
  },
  {
    id: "current_routine",
    question: "What products do you currently use?",
    subtitle: "Be honest — no judgement here!",
    options: [
      { value: "nothing", label: "Nothing Specific", desc: "Whatever's in the shower" },
      { value: "basic", label: "Basic Shampoo & Conditioner", desc: "Supermarket brand, not curl-specific" },
      { value: "curl_specific", label: "Curl-Specific Products", desc: "I've started using curl products" },
      { value: "full_routine", label: "Full Curl Routine", desc: "I've got my routine dialled in" },
    ],
  },
];

function getRecommendations(answers) {
  const { curl_type, concern, styling, current_routine } = answers;
  const recs = [];

  // Brand logic:
  // Wavy (2A-2C) → Davines LOVE Curl
  // Curly (3A-3C) → Davroe CURLiCUE
  // Coily (4A-4C) → Davroe CURLiCUE
  // Override: volume (flat, no frizz) → always Davines
  // Override: dryness or frizz → always Davroe
  const useDavines = curl_type === "wavy" || concern === "volume";
  const useDavroe = curl_type === "curly" || curl_type === "coily" || concern === "frizz" || concern === "dryness";

  // If both triggers hit, concern overrides curl type
  const brand = (concern === "volume") ? "davines"
    : (concern === "frizz" || concern === "dryness") ? "davroe"
    : (curl_type === "wavy") ? "davines"
    : "davroe";

  if (brand === "davines") {
    // --- Davines LOVE Curl routine ---
    recs.push({ lookup_key: "davines_shampoo", step: "Cleanse", reason: "A moisturising cleanser that enhances elasticity — perfect for lighter curl patterns that need gentle care." });
    recs.push({ lookup_key: "davines_conditioner", step: "Condition", reason: "Fast-acting 30-second conditioner that detangles and boosts bounce without weighing your waves down." });

    if (concern === "volume") {
      recs.push({ lookup_key: "davines_milk", step: "Activate", reason: "Your curls need lift, not weight. This multi-purpose milk adds volume and movement without heaviness." });
      recs.push({ lookup_key: "davines_cream", step: "Define", reason: "Soft hold that gives your curls shape and 72 hours of anti-frizz protection." });
    } else if (concern === "definition") {
      recs.push({ lookup_key: "davines_cream", step: "Define", reason: "This defining cream gives soft, touchable hold and brings out your natural curl pattern." });
      recs.push({ lookup_key: "davines_milk", step: "Protect", reason: "Layer this under or over your cream for extra protection and enhanced movement." });
    } else {
      recs.push({ lookup_key: "davines_mask", step: "Deep Treat", reason: "Use weekly for intense nourishment — up to 5.3x more nourished hair with the full LOVE Curl routine." });
      recs.push({ lookup_key: "davines_cream", step: "Style", reason: "72 hours of anti-frizz protection in a non-rinse cream. Apply to damp hair and go." });
    }

    if (styling === "wash_go" || styling === "air_dry") {
      if (!recs.find(r => r.lookup_key === "davines_cowash")) {
        recs.push({ lookup_key: "davines_cowash", step: "Refresh", reason: "Perfect for refresh days between full washes — gently cleanses while enhancing your curl pattern." });
      }
    }
  } else {
    // --- Davroe CURLiCUE routine ---
    recs.push({ lookup_key: "davroe_cleansing_clay", step: "Cleanse", reason: "This clay cleanser deeply purifies without stripping — exactly what curly and coily hair needs." });
    recs.push({ lookup_key: "davroe_deep_conditioning", step: "Condition", reason: "Ultra-rich conditioning rinse that restores moisture and detangles even the tightest curls." });

    if (concern === "frizz") {
      recs.push({ lookup_key: "davroe_curl_balm", step: "Style", reason: "Your number one frizz fighter — this leave-in balm smooths flyaways and gives soft, touchable definition." });
      recs.push({ lookup_key: "davroe_hair_oil", step: "Seal", reason: "Lock everything in with this finishing oil. Non-greasy shine and all-day frizz control." });
    } else if (concern === "dryness") {
      recs.push({ lookup_key: "davroe_hair_oil", step: "Nourish", reason: "Ultra-hydrating oil that penetrates deep to nourish and protect dry, thirsty curls." });
      recs.push({ lookup_key: "davroe_curl_balm", step: "Style", reason: "Hydrating balm with Avocado Oil that seals in moisture while giving beautiful definition." });
    } else if (concern === "definition") {
      recs.push({ lookup_key: "davroe_curl_activator", step: "Activate", reason: "Spray onto wet hair and scrunch — this activator brings out your natural curl pattern beautifully." });
      recs.push({ lookup_key: "davroe_curl_gel", step: "Hold", reason: "Lock in those curls with this lightweight gel. Great hold without crunch." });
    } else {
      recs.push({ lookup_key: "davroe_curl_activator", step: "Activate", reason: "Lightweight spray that adds bounce and body to your natural curls." });
      recs.push({ lookup_key: "davroe_curl_balm", step: "Style", reason: "Leave-in balm that defines and protects — a curl routine essential." });
    }

    if (styling === "diffuse" || styling === "wash_go") {
      if (!recs.find(r => r.lookup_key === "davroe_curl_gel")) {
        recs.push({ lookup_key: "davroe_curl_gel", step: "Hold", reason: "Perfect for wash-day definition. Apply to soaking wet hair for the best clumps." });
      }
    }
    if (styling === "air_dry" && !recs.find(r => r.lookup_key === "davroe_curl_balm")) {
      recs.push({ lookup_key: "davroe_curl_balm", step: "Style", reason: "Ideal for air-drying — this balm protects and defines while your hair dries naturally." });
    }
  }

  // Tools — always Davroe (brand-neutral accessories)
  if (current_routine === "nothing" || current_routine === "basic") {
    recs.push({ lookup_key: "davroe_curl_brush", step: "Tool", reason: "Ditch the regular brush — this one is designed to detangle wet curls without breakage." });
  }
  if (curl_type === "coily" || styling === "protective" || concern === "dryness") {
    recs.push({ lookup_key: "davroe_hair_wrap", step: "Dry", reason: "Reduce drying time by half and minimise frizz with this microfibre wrap. Game changer." });
  }

  const seen = new Set();
  return recs.filter((r) => { if (seen.has(r.lookup_key)) return false; seen.add(r.lookup_key); return true; });
}

export default function CurlQuizPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [products, setProducts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/shop/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // Check for shared results in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shared = params.get("r");
    if (shared && products.length > 0) {
      try {
        const decoded = JSON.parse(atob(shared));
        if (decoded.curl_type) {
          setAnswers(decoded);
          const recs = getRecommendations(decoded);
          const enriched = recs.map((rec) => {
            const product = products.find((p) => p.lookup_key === rec.lookup_key);
            return product ? { ...product, reason: rec.reason, step: rec.step } : null;
          }).filter(Boolean);
          setRecommendations(enriched);
          setStep(QUESTIONS.length - 1);
          setShowResults(true);
        }
      } catch {}
    }
  }, [products]);

  const currentQ = QUESTIONS[step];
  const isLast = step === QUESTIONS.length - 1;
  const progress = ((step + (showResults ? 1 : 0)) / QUESTIONS.length) * 100;

  const selectOption = (value) => {
    const newAnswers = { ...answers, [currentQ.id]: value };
    setAnswers(newAnswers);
    if (isLast) {
      const recs = getRecommendations(newAnswers);
      const enriched = recs.map((rec) => {
        const product = products.find((p) => p.lookup_key === rec.lookup_key);
        return product ? { ...product, reason: rec.reason, step: rec.step } : null;
      }).filter(Boolean);
      setRecommendations(enriched);
      setTimeout(() => setShowResults(true), 300);
    } else {
      setTimeout(() => setStep(step + 1), 300);
    }
  };

  const goBack = () => {
    if (showResults) setShowResults(false);
    else if (step > 0) setStep(step - 1);
  };

  const restart = () => {
    setStep(0); setAnswers({}); setRecommendations([]); setShowResults(false); setSaved(false); setCopied(false); setEmailSent(false); setEmailInput("");
    // Clear URL params
    window.history.replaceState({}, "", window.location.pathname);
  };

  const getShareUrl = () => {
    const encoded = btoa(JSON.stringify(answers));
    return `${window.location.origin}/curl-quiz?r=${encoded}`;
  };

  const copyShareLink = async () => {
    const url = getShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied! Share it with your friends.");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement("input");
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const shareToInstagram = () => {
    const curlLabel = { wavy: "Wavy", curly: "Curly", coily: "Coily" }[answers.curl_type] || "";
    const productNames = recommendations.map((r) => r.name).join(", ");
    const text = `I just took the Justjjax Curl Quiz and got my personalised ${curlLabel} hair routine! My recommended products: ${productNames}. Take the quiz: ${getShareUrl()}`;

    // Try native share (works on mobile/Instagram)
    if (navigator.share) {
      navigator.share({ title: "My Curl Routine — Justjjax", text, url: getShareUrl() }).catch(() => {});
    } else {
      // Fallback: copy text for manual sharing
      navigator.clipboard.writeText(text).then(() => {
        toast.success("Share text copied! Paste it into your Instagram Story or post.");
      }).catch(() => {
        toast.info("Copy the link above to share on Instagram.");
      });
    }
  };

  const saveRoutine = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/routines/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          answers,
          product_ids: recommendations.map((r) => r.id),
        }),
      });
      if (!res.ok) throw new Error();
      setSaved(true);
      toast.success("Routine saved! You can access it anytime from your account.");
    } catch {
      toast.error("Failed to save — please try again.");
    }
    setSaving(false);
  };

  const emailRoutine = async () => {
    if (!emailInput.trim()) {
      toast.error("Please enter your email address.");
      return;
    }
    setEmailSending(true);
    try {
      await axios.post(`${API}/routines/email`, {
        email: emailInput.trim(),
        answers,
        products: recommendations.map((r) => ({
          name: r.name,
          brand: r.brand,
          step: r.step,
          reason: r.reason,
        })),
      });
      setEmailSent(true);
      toast.success("Routine sent to your inbox!");
    } catch {
      toast.error("Failed to send — please try again.");
    }
    setEmailSending(false);
  };

  return (
    <div className="pt-28 lg:pt-44 pb-14 min-h-screen" data-testid="curl-quiz-page">
      {/* Progress bar */}
      <div className="max-w-2xl mx-auto px-6 mb-8">
        <div className="h-1 bg-black/5 rounded-full overflow-hidden">
          <motion.div className="h-full bg-olive rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-[10px] text-charcoal/40 uppercase tracking-wider">
            {showResults ? "Your results" : `Question ${step + 1} of ${QUESTIONS.length}`}
          </span>
          {(step > 0 || showResults) && (
            <button onClick={goBack} className="text-[10px] text-charcoal/40 hover:text-charcoal uppercase tracking-wider flex items-center gap-1" data-testid="quiz-back-btn">
              <ChevronLeft size={10} /> Back
            </button>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!showResults ? (
          <motion.div key={`q-${step}`} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.3 }} className="max-w-2xl mx-auto px-6">
            <div className="text-center mb-8">
              <h1 className="font-display text-3xl sm:text-4xl text-black mb-2" data-testid="quiz-question">{currentQ.question}</h1>
              <p className="text-charcoal/50 text-sm">{currentQ.subtitle}</p>
            </div>

            {currentQ.hasImages ? (
              /* Image grid for curl type question */
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {currentQ.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => selectOption(opt.value)}
                    className={`text-center rounded-2xl overflow-hidden border-2 transition-all duration-200 ${
                      answers[currentQ.id] === opt.value
                        ? "border-olive bg-olive/5 shadow-md"
                        : "border-black/5 bg-white/70 hover:border-sage/30 hover:shadow-sm"
                    }`}
                    data-testid={`quiz-option-${opt.value}`}
                  >
                    <div className="aspect-square bg-white/50 flex items-center justify-center p-3 overflow-hidden">
                      <img src={opt.image} alt={opt.label} className="w-full h-full object-contain rounded-xl" />
                    </div>
                    <div className="p-4">
                      <p className="font-medium text-sm text-black">{opt.label}</p>
                      <p className="text-xs text-charcoal/50 mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              /* Text options for other questions */
              <div className="space-y-3">
                {currentQ.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => selectOption(opt.value)}
                    className={`w-full text-left rounded-2xl p-5 border-2 transition-all duration-200 ${
                      answers[currentQ.id] === opt.value
                        ? "border-olive bg-olive/5"
                        : "border-black/5 bg-white/70 hover:border-sage/30 hover:bg-white"
                    }`}
                    data-testid={`quiz-option-${opt.value}`}
                  >
                    <p className="font-medium text-sm text-black">{opt.label}</p>
                    <p className="text-xs text-charcoal/50 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        ) : (
          /* Results */
          <motion.div key="results" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="w-14 h-14 rounded-full bg-olive/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles size={24} className="text-olive" />
              </div>
              <h1 className="font-display text-3xl sm:text-4xl text-black mb-2" data-testid="quiz-results-heading">Your Curl Routine</h1>
              <p className="text-charcoal/50 text-sm max-w-md mx-auto">
                Based on your answers, here's a personalised wash-day routine hand-picked for your curls.
              </p>
            </div>

            {/* Step-by-step routine */}
            <div className="space-y-4">
              {recommendations.map((product, i) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white/70 rounded-2xl border border-black/5 overflow-hidden flex"
                  data-testid={`quiz-rec-${product.id}`}
                >
                  {/* Step number */}
                  <div className="w-10 sm:w-12 flex-shrink-0 bg-olive/5 flex flex-col items-center justify-center">
                    <span className="text-[10px] uppercase tracking-widest text-olive/60 font-medium [writing-mode:vertical-lr] rotate-180">
                      {product.step}
                    </span>
                  </div>
                  {/* Product image */}
                  {product.image && (
                    <div className="w-20 sm:w-28 flex-shrink-0 bg-white flex items-center justify-center p-2">
                      <img src={product.image} alt={product.name} className="max-h-full max-w-full object-contain" />
                    </div>
                  )}
                  {/* Product info */}
                  <div className="p-4 sm:p-5 flex-1">
                    <p className="text-[10px] uppercase tracking-widest text-charcoal/40">{product.brand} · {product.range}</p>
                    <h3 className="font-display text-base text-black mt-0.5">{product.name}</h3>
                    {product.size && <span className="text-[10px] text-charcoal/40">{product.size}</span>}
                    <p className="text-xs text-olive/80 mt-2 italic leading-relaxed">"{product.reason}"</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Save / Actions */}
            <div className="mt-10 space-y-4">
              {/* Save routine CTA */}
              {user ? (
                <button
                  onClick={saveRoutine}
                  disabled={saving || saved}
                  className={`w-full rounded-full py-3.5 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    saved
                      ? "bg-olive/10 text-olive border border-olive/20"
                      : "bg-olive text-white hover:bg-olive/80"
                  }`}
                  data-testid="quiz-save-btn"
                >
                  {saved ? (
                    <><Check size={16} /> Routine Saved</>
                  ) : saving ? (
                    "Saving..."
                  ) : (
                    <><Save size={16} /> Save My Routine</>
                  )}
                </button>
              ) : (
                <Link
                  to="/login"
                  className="w-full bg-olive text-white hover:bg-olive/80 rounded-full py-3.5 text-sm font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="quiz-login-to-save-btn"
                >
                  <LogIn size={16} /> Sign in to Save Your Routine
                </Link>
              )}

              {/* Email routine */}
              <div className="bg-white/60 rounded-2xl border border-black/5 p-5" data-testid="email-routine-section">
                <p className="text-sm font-medium text-black mb-3 flex items-center gap-2">
                  <Mail size={16} className="text-olive" /> Email this routine to yourself
                </p>
                {emailSent ? (
                  <p className="text-sm text-olive flex items-center gap-2" data-testid="email-sent-msg">
                    <Check size={14} /> Sent! Check your inbox.
                  </p>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="flex-1 rounded-full border border-black/10 px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-olive/40"
                      data-testid="email-routine-input"
                    />
                    <button
                      onClick={emailRoutine}
                      disabled={emailSending}
                      className="bg-olive text-white hover:bg-olive/80 rounded-full px-5 py-2.5 text-sm font-medium transition-all disabled:opacity-50 whitespace-nowrap"
                      data-testid="email-routine-btn"
                    >
                      {emailSending ? "Sending..." : "Send"}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap justify-center gap-3">
                <button
                  onClick={copyShareLink}
                  className={`rounded-full px-5 py-3 text-sm font-medium transition-all inline-flex items-center gap-2 ${
                    copied
                      ? "bg-olive/10 text-olive border border-olive/20"
                      : "border border-charcoal/20 text-charcoal hover:bg-white/50"
                  }`}
                  data-testid="quiz-share-link-btn"
                >
                  {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Link</>}
                </button>
                <button
                  onClick={shareToInstagram}
                  className="border border-charcoal/20 text-charcoal hover:bg-white/50 rounded-full px-5 py-3 text-sm font-medium transition-all inline-flex items-center gap-2"
                  data-testid="quiz-share-insta-btn"
                >
                  <Share2 size={14} /> Share
                </button>
                <button
                  onClick={restart}
                  className="border border-charcoal/20 text-charcoal hover:bg-white/50 rounded-full px-5 py-3 text-sm font-medium transition-all inline-flex items-center gap-2"
                  data-testid="quiz-restart-btn"
                >
                  <RotateCcw size={14} /> Retake Quiz
                </button>
                <a
                  href="https://tinyurl.com/2nxodcgu"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-ocean text-white hover:bg-charcoal rounded-full px-5 py-3 text-sm font-medium transition-all inline-flex items-center gap-2"
                  data-testid="quiz-book-consult-btn"
                >
                  Book a Curl Consultation
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
